import { Schema } from "effect";
import { DesktopGitRelativePathSchema } from "./desktop-git";
import { DesktopWorkspaceGrantIdSchema } from "./desktop-workspace";
import {
  CanonicalIdentifierSchema,
  HttpUrlSchema,
  IsoDateTimeSchema,
  NonNegativeSafeIntegerSchema,
  PapersFlowBoundaryParseOptions,
} from "./standard";
import { utf8ByteLength } from "./utf8";

export const DESKTOP_GITHUB_MAX_PULL_REQUESTS = 50;
export const DESKTOP_GITHUB_MAX_FILES = 300;
export const DESKTOP_GITHUB_MAX_TITLE_BYTES = 512;
export const DESKTOP_GITHUB_MAX_BODY_BYTES = 64 * 1_024;
export const DESKTOP_GITHUB_MAX_DIFF_BYTES = 512 * 1_024;

const boundedText = (maximumBytes: number) =>
  Schema.String.check(
    Schema.makeFilter(
      (value) =>
        utf8ByteLength(value) <= maximumBytes || `must contain at most ${maximumBytes} UTF-8 bytes`,
    ),
  );

export const GitHubPullRequestNumberSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);

export const GitHubRepositoryNameSchema = Schema.String.check(
  Schema.isMinLength(3),
  Schema.isMaxLength(200),
  Schema.makeFilter(
    (value) =>
      /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/u.test(value) ||
      "must be a canonical GitHub owner/repository name",
  ),
);

export const GitHubLoginSchema = CanonicalIdentifierSchema.check(Schema.isMaxLength(100));

const GitRefNameSchema = CanonicalIdentifierSchema.check(Schema.isMaxLength(255));
export const GitHubRevisionSchema = CanonicalIdentifierSchema.check(Schema.isMaxLength(180));

export const DesktopGitHubRepositorySchema = Schema.Struct({
  nameWithOwner: GitHubRepositoryNameSchema,
  url: HttpUrlSchema,
});

export const DesktopGitHubPullRequestSummarySchema = Schema.Struct({
  number: GitHubPullRequestNumberSchema,
  title: boundedText(DESKTOP_GITHUB_MAX_TITLE_BYTES).check(Schema.isMinLength(1)),
  author: Schema.NullOr(GitHubLoginSchema),
  headRefName: GitRefNameSchema,
  baseRefName: GitRefNameSchema,
  isDraft: Schema.Boolean,
  updatedAt: IsoDateTimeSchema,
  url: HttpUrlSchema,
});

export const DesktopGitHubPullRequestListInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
});

export const DesktopGitHubPullRequestListResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  repository: DesktopGitHubRepositorySchema,
  pullRequests: Schema.Array(DesktopGitHubPullRequestSummarySchema).check(
    Schema.isMaxLength(DESKTOP_GITHUB_MAX_PULL_REQUESTS),
  ),
  truncated: Schema.Boolean,
}).check(
  Schema.makeFilter((result) => {
    const numbers = new Set<number>();
    for (const pullRequest of result.pullRequests) {
      if (numbers.has(pullRequest.number)) return "pull request numbers must be unique";
      numbers.add(pullRequest.number);
    }
    return true;
  }),
);

export const DesktopGitHubPullRequestInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
  pullRequestNumber: GitHubPullRequestNumberSchema,
});

export const DesktopGitHubPullRequestFileSchema = Schema.Struct({
  path: DesktopGitRelativePathSchema,
  additions: NonNegativeSafeIntegerSchema,
  deletions: NonNegativeSafeIntegerSchema,
});

export const DesktopGitHubPullRequestResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  repository: DesktopGitHubRepositorySchema,
  pullRequest: DesktopGitHubPullRequestSummarySchema,
  body: boundedText(DESKTOP_GITHUB_MAX_BODY_BYTES),
  bodyTruncated: Schema.Boolean,
  headRevision: GitHubRevisionSchema,
  state: Schema.Literals(["open", "closed", "merged"]),
  mergeable: Schema.Literals(["mergeable", "conflicting", "unknown"]),
  additions: NonNegativeSafeIntegerSchema,
  deletions: NonNegativeSafeIntegerSchema,
  files: Schema.Array(DesktopGitHubPullRequestFileSchema).check(
    Schema.isMaxLength(DESKTOP_GITHUB_MAX_FILES),
  ),
  filesTruncated: Schema.Boolean,
});

export const DesktopGitHubPullRequestDiffInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
  pullRequestNumber: GitHubPullRequestNumberSchema,
  expectedHeadRevision: GitHubRevisionSchema,
});

export const DesktopGitHubPullRequestDiffResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  repository: DesktopGitHubRepositorySchema,
  pullRequestNumber: GitHubPullRequestNumberSchema,
  headRevision: GitHubRevisionSchema,
  patch: boundedText(DESKTOP_GITHUB_MAX_DIFF_BYTES),
  truncated: Schema.Boolean,
});

export const DesktopGitHubErrorCodeSchema = Schema.Literals([
  "auth-required",
  "github-failed",
  "invalid-input",
  "invalid-response",
  "invalid-root",
  "not-github-repository",
  "output-too-large",
  "pull-request-not-found",
  "root-not-granted",
  "stale-pull-request",
  "unavailable",
  "unsupported-content",
  "unsupported-path",
]);

export const DesktopGitHubErrorPayloadSchema = Schema.Struct({
  code: DesktopGitHubErrorCodeSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});

export type DesktopGitHubRepository = typeof DesktopGitHubRepositorySchema.Type;
export type DesktopGitHubRepositoryEncoded = typeof DesktopGitHubRepositorySchema.Encoded;
export type DesktopGitHubPullRequestSummary = typeof DesktopGitHubPullRequestSummarySchema.Type;
export type DesktopGitHubPullRequestSummaryEncoded =
  typeof DesktopGitHubPullRequestSummarySchema.Encoded;
export type DesktopGitHubPullRequestListInput = typeof DesktopGitHubPullRequestListInputSchema.Type;
export type DesktopGitHubPullRequestListInputEncoded =
  typeof DesktopGitHubPullRequestListInputSchema.Encoded;
export type DesktopGitHubPullRequestListResult =
  typeof DesktopGitHubPullRequestListResultSchema.Type;
export type DesktopGitHubPullRequestListResultEncoded =
  typeof DesktopGitHubPullRequestListResultSchema.Encoded;
export type DesktopGitHubPullRequestInput = typeof DesktopGitHubPullRequestInputSchema.Type;
export type DesktopGitHubPullRequestInputEncoded =
  typeof DesktopGitHubPullRequestInputSchema.Encoded;
export type DesktopGitHubPullRequestFile = typeof DesktopGitHubPullRequestFileSchema.Type;
export type DesktopGitHubPullRequestFileEncoded = typeof DesktopGitHubPullRequestFileSchema.Encoded;
export type DesktopGitHubPullRequestResult = typeof DesktopGitHubPullRequestResultSchema.Type;
export type DesktopGitHubPullRequestResultEncoded =
  typeof DesktopGitHubPullRequestResultSchema.Encoded;
export type DesktopGitHubPullRequestDiffInput = typeof DesktopGitHubPullRequestDiffInputSchema.Type;
export type DesktopGitHubPullRequestDiffInputEncoded =
  typeof DesktopGitHubPullRequestDiffInputSchema.Encoded;
export type DesktopGitHubPullRequestDiffResult =
  typeof DesktopGitHubPullRequestDiffResultSchema.Type;
export type DesktopGitHubPullRequestDiffResultEncoded =
  typeof DesktopGitHubPullRequestDiffResultSchema.Encoded;
export type DesktopGitHubErrorCode = typeof DesktopGitHubErrorCodeSchema.Type;
export type DesktopGitHubErrorPayload = typeof DesktopGitHubErrorPayloadSchema.Type;
export type DesktopGitHubErrorPayloadEncoded = typeof DesktopGitHubErrorPayloadSchema.Encoded;

export const decodeDesktopGitHubPullRequestListInput = Schema.decodeUnknownSync(
  DesktopGitHubPullRequestListInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopGitHubPullRequestListInput = Schema.encodeSync(
  DesktopGitHubPullRequestListInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopGitHubPullRequestListResult = Schema.decodeUnknownSync(
  DesktopGitHubPullRequestListResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopGitHubPullRequestListResult = Schema.encodeSync(
  DesktopGitHubPullRequestListResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopGitHubPullRequestInput = Schema.decodeUnknownSync(
  DesktopGitHubPullRequestInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopGitHubPullRequestInput = Schema.encodeSync(
  DesktopGitHubPullRequestInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopGitHubPullRequestResult = Schema.decodeUnknownSync(
  DesktopGitHubPullRequestResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopGitHubPullRequestResult = Schema.encodeSync(
  DesktopGitHubPullRequestResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopGitHubPullRequestDiffInput = Schema.decodeUnknownSync(
  DesktopGitHubPullRequestDiffInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopGitHubPullRequestDiffInput = Schema.encodeSync(
  DesktopGitHubPullRequestDiffInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopGitHubPullRequestDiffResult = Schema.decodeUnknownSync(
  DesktopGitHubPullRequestDiffResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopGitHubPullRequestDiffResult = Schema.encodeSync(
  DesktopGitHubPullRequestDiffResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopGitHubErrorPayload = Schema.decodeUnknownSync(
  DesktopGitHubErrorPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopGitHubErrorPayload = Schema.encodeSync(
  DesktopGitHubErrorPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
